/**
 * "Read about {city}" related-articles section for city / category × city pages.
 *
 * Pulls blog posts whose title/excerpt mentions the city, links each card to
 * /blog/{slug}. Renders nothing if no matches — keeps the page clean for new
 * cities that don't yet have editorial coverage.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import {
  panelFrameStyle,
  panelInnerStyle,
} from "../../../shared/ui/card-frame";
import { useCityArticles } from "../../blog/hooks/use-city-articles";

import { Icon } from "@majourneys/icons";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
interface CategoryCityArticlesProps {
  cityName: string | null;
  lang: string;
  limit?: number;
}

export function CategoryCityArticles({
  cityName,
  lang,
  limit = 3,
}: CategoryCityArticlesProps) {
  const { t } = useTranslation();
  const articles = useCityArticles(cityName, limit);
  if (!cityName || !articles || articles.length === 0) return null;

  return (
    <section aria-label={t("activity.city_articles_aria", { city: cityName })} className="py-10">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p
            className="text-[11px] font-bold uppercase"
            style={{ color: MUTED, letterSpacing: "0.09em" }}
          >
            {t("activity.from_the_journal", {
              defaultValue: "From the journal",
            })}
          </p>
          <h2
            className="mt-1 text-[20px] font-semibold leading-tight sm:text-[22px]"
            style={{
              color: NAVY,
              fontFamily: "var(--font-geist)",
              letterSpacing: "-0.02em",
            }}
          >
            {t("activity.read_about", {
              defaultValue: "Read about {{city}}",
              city: cityName,
            })}
          </h2>
        </div>
        <Link
          to="/$lang/blog"
          params={{ lang }}
          className="hidden shrink-0 text-[13px] font-semibold text-[color:var(--color-primary)] hover:underline sm:inline"
        >
          {t("activity.all_articles", { defaultValue: "All articles →" })}
        </Link>
      </div>

      {/*
        A rail on a phone, the grid from `sm`, matching similar listings and the
        home rows. One column put one full-width article on screen and hid the
        rest behind a scroll; at 240px the next one is half in view, which is
        what says there are more.
      */}
      <div className="-mx-4 mt-5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:overflow-x-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
        <ul className="flex min-w-max gap-4 sm:grid sm:min-w-0 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((post) => (
            <li key={post.id} className="w-[240px] shrink-0 sm:w-auto">
              <Link
                to="/$lang/blog/$slug"
                params={{ lang, slug: post.slug }}
                className="group block h-full transition-transform duration-200 hover:-translate-y-[2px]"
                style={panelFrameStyle(22, "topLit")}
              >
                {/* The clip lives on the inner card, never on the frame — a
                  clipping ancestor eats the frame's own outer hairline. */}
                <div
                  className="flex h-full flex-col overflow-hidden"
                  style={panelInnerStyle(22)}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <ImageWithFallback
                      src={post.image}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-4">
                    <div
                      className="flex items-center gap-2 text-[11px] uppercase"
                      style={{ color: MUTED, letterSpacing: "0.09em" }}
                    >
                      <span className="font-bold text-[color:var(--color-primary)]">
                        {post.category.name}
                      </span>
                      <span aria-hidden>·</span>
                      <span>{post.date}</span>
                    </div>
                    <h3
                      className="line-clamp-2 text-[15.5px] font-semibold leading-snug group-hover:text-[color:var(--color-primary)]"
                      style={{ color: NAVY, letterSpacing: "-0.015em" }}
                    >
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p
                        className="line-clamp-2 text-[13px]"
                        style={{ color: MUTED }}
                      >
                        {post.excerpt}
                      </p>
                    )}
                    <span
                      className="mt-auto inline-flex items-center gap-1.5 pt-2 text-[12.5px] font-semibold transition-all group-hover:gap-2"
                      style={{ color: NAVY }}
                    >
                      {t("activity.read_story", { defaultValue: "Read story" })}{" "}
                      <Icon name="ri-arrow-right-line" className="rtl:-scale-x-100" aria-hidden />
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
